import DOMPurify from 'dompurify';

/**
 * Allowed HTML tags and attributes for button labels.
 * Only safe inline formatting tags are permitted — no block elements,
 * scripts, event handlers, or dangerous attributes.
 */
const ALLOWED_TAGS = ['span', 'strong', 'em', 'b', 'i', 'small', 'br', 'sub', 'sup'];
const ALLOWED_ATTR = ['style'];

/**
 * Allowed CSS properties within style attributes.
 * Only visual formatting — no position, url(), expression(), or binding.
 */
const ALLOWED_STYLE_PROPERTIES = new Set([
    'font-size',
    'font-weight',
    'font-style',
    'color',
    'opacity',
    'text-transform',
    'letter-spacing',
    'text-decoration',
    'vertical-align',
    'line-height',
    'display',
]);

function sanitizeStyleAttribute(style: string): string {
    return style
        .split(';')
        .map((decl) => decl.trim())
        .filter((decl) => {
            if (!decl) return false;
            const colonIdx = decl.indexOf(':');
            if (colonIdx === -1) return false;
            const prop = decl.substring(0, colonIdx).trim().toLowerCase();
            const value = decl.substring(colonIdx + 1).trim().toLowerCase();
            if (!ALLOWED_STYLE_PROPERTIES.has(prop)) return false;
            // Block url(), expression(), and similar injection vectors
            if (/url\s*\(|expression\s*\(|javascript:|data:/i.test(value)) return false;
            return true;
        })
        .join('; ');
}

/**
 * Sanitize an HTML string for safe use in button labels.
 * Returns clean HTML with only whitelisted inline tags and style properties.
 */
export function sanitizeButtonLabel(dirty: string): string {
    const clean = DOMPurify.sanitize(dirty, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        KEEP_CONTENT: true,
    });

    // Second pass: sanitize individual style attribute values
    const div = document.createElement('div');
    div.innerHTML = clean;
    div.querySelectorAll('[style]').forEach((el) => {
        const sanitized = sanitizeStyleAttribute(el.getAttribute('style') || '');
        if (sanitized) {
            el.setAttribute('style', sanitized);
        } else {
            el.removeAttribute('style');
        }
    });

    return div.innerHTML;
}
